import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Clock, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { SOURCE_META, sourceLabel } from "@/lib/funnel";
import { StageBadge } from "@/components/StageBadge";

export default function NeedsAction() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "needs_action";
  const [leads, setLeads] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/leads", { params: { view } }).then(({ data }) => setLeads(data));
  }, [view]);

  return (
    <div data-testid="needs-action-page">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-semibold text-slate-900">Action Center</h1>
        <p className="text-slate-500 text-sm">Pending and overdue tasks across the funnel</p>
      </div>

      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setParams({ view: "needs_action" })}
          data-testid="tab-needs-action"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${view === "needs_action" ? "bg-amber-500 text-white" : "bg-white border border-slate-200 text-slate-600"}`}
        >
          <AlertTriangle className="h-4 w-4" /> Needs Action
        </button>
        <button
          onClick={() => setParams({ view: "overdue" })}
          data-testid="tab-overdue"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${view === "overdue" ? "bg-red-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}
        >
          <Clock className="h-4 w-4" /> Overdue
        </button>
      </div>

      <div className="space-y-3">
        {leads.map((l) => (
          <button
            key={l.id}
            onClick={() => navigate(`/leads/${l.id}`)}
            data-testid={`action-lead-${l.id}`}
            className={`w-full text-left bg-white border-l-4 ${l.overdue ? "border-l-red-500" : "border-l-amber-400"} border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between gap-4`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-slate-900 truncate">{l.company_name}</p>
                <StageBadge stage={l.stage} />
              </div>
              <p className={`text-sm mt-1 font-medium ${l.overdue ? "text-red-600" : "text-amber-600"}`}>
                {l.pending_reason} {l.overdue ? `· ${l.hours_overdue}h overdue` : ""}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {sourceLabel(l)} · {l.assigned_to_name || "Unassigned"}
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
          </button>
        ))}
        {leads.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p>Nothing here — you're all caught up! 🎉</p>
          </div>
        )}
      </div>
    </div>
  );
}
